"use client";

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useTotalChatUsage } from "@/features/dashboard/queries";
import { useSubscription } from "@/features/subscription/queries";
import { useActiveCalendarAccounts } from "@/features/integrations/queries";
import { useTeamMembers } from "@/features/team/queries";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { canonicalizeGoal } from "@/features/onboarding/goal-canonical";
import { OnboardingGoals } from "@/features/onboarding/components/onboarding-questions";

export type ActivationStepId = "upload" | "chat" | "calendar" | "team";
export type ActivationGoalBucket = "sales" | "interview" | "meeting" | "default";

export interface ActivationStatus {
  hasMedia: boolean;
  hasUsedChat: boolean;
  hasCalendar: boolean;
  hasTeammate: boolean;
  completedCount: number;
  totalSteps: number;
  allComplete: boolean;
  orderedStepIds: readonly ActivationStepId[];
  onboardingGoal: string | null;
  goalBucket: ActivationGoalBucket;
  isDismissed: boolean;
  isLoading: boolean;
  isError: boolean;
  dismiss: () => void;
  restore: () => void;
}

const DISMISS_KEY = (userId: string) => `speak_activation_dismissed_${userId}`;

const CANONICAL_ORDER: readonly ActivationStepId[] = ["upload", "chat", "calendar", "team"] as const;

function bucketGoal(goal: string | null): ActivationGoalBucket {
  if (!goal) return "default";
  // Shared canonicalization first; the keyword fallback below only handles free-text goals.
  const canonical = canonicalizeGoal(goal);
  if (canonical === OnboardingGoals.SALES) return "sales";
  if (canonical === OnboardingGoals.RESEARCH) return "interview";
  if (canonical === OnboardingGoals.MEETINGS) return "meeting";
  const g = goal.toLowerCase();
  if (/\bsales\b|\bcall scor/.test(g)) return "sales";
  if (/(interview|research|focus group|qualitative)/.test(g)) return "interview";
  if (/(meeting|zoom|teams|webex|stand[- ]?up|sync)/.test(g)) return "meeting";
  return "default";
}

function computeOrderedStepIds(
  bucket: ActivationGoalBucket,
  done: Record<ActivationStepId, boolean>,
): readonly ActivationStepId[] {
  const priority: readonly ActivationStepId[] =
    bucket === "sales"
      ? (["upload", "calendar", "chat", "team"] as const)
      : bucket === "interview"
        ? (["upload", "chat", "team", "calendar"] as const)
        : CANONICAL_ORDER;
  const uncompleted = priority.filter((id) => !done[id]);
  const completed = CANONICAL_ORDER.filter((id) => done[id]);
  return [...uncompleted, ...completed];
}

export function useActivationStatus(): ActivationStatus {
  const { user } = useAuth();
  const userId = user?._id ?? "";

  // SSR-safe constant; useEffect below syncs from localStorage post-mount.
  // The previous useState initializer caused hydration mismatches because SSR
  // returned false but CSR returned true for users who had dismissed.
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !userId) return;
    const key = DISMISS_KEY(userId);
    setIsDismissed(localStorage.getItem(key) === "true");

    function onStorage(event: StorageEvent) {
      if (event.key !== key) return;
      setIsDismissed(event.newValue === "true");
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [userId]);

  const enabled = !isDismissed && !!userId;

  const { data: subscription, isLoading: subLoading } = useSubscription({ enabled });
  const { data: chatStats, isLoading: chatLoading, isError: chatError } = useTotalChatUsage(enabled);

  const { data: calendarAccounts, isLoading: calLoading, isError: calError } =
    useActiveCalendarAccounts({ enabled });

  const { data: teamData, isLoading: teamLoading, isError: teamError } =
    useTeamMembers({}, { enabled });

  const hasMedia = (subscription?.totalMedia ?? 0) > 0;
  const hasUsedChat = (chatStats?.totalPrompts ?? 0) > 0 || (chatStats?.totalCharacters ?? 0) > 0;
  const hasCalendar = (calendarAccounts?.length ?? 0) > 0;
  const hasTeammate = (teamData?.totalCount ?? 0) > 1;

  const signals = [hasMedia, hasUsedChat, hasCalendar, hasTeammate];
  const completedCount = signals.filter(Boolean).length;
  const totalSteps = 4;
  const allComplete = completedCount === totalSteps;
  const isLoading = enabled && (subLoading || chatLoading || calLoading || teamLoading);
  const isError = Boolean(chatError || calError || teamError);

  const onboardingGoalRaw = user?.onboarding?.goal ?? null;
  const onboardingGoal = onboardingGoalRaw && onboardingGoalRaw.trim().length > 0 ? onboardingGoalRaw : null;
  const goalBucket = bucketGoal(onboardingGoal);
  const orderedStepIds = useMemo(() => {
    return computeOrderedStepIds(goalBucket, {
      upload: hasMedia, chat: hasUsedChat, calendar: hasCalendar, team: hasTeammate,
    });
  }, [goalBucket, hasMedia, hasUsedChat, hasCalendar, hasTeammate]);

  function dismiss() {
    if (typeof window !== "undefined" && userId) {
      localStorage.setItem(DISMISS_KEY(userId), "true");
      setIsDismissed(true);
      trackFunnel("activation", "checklist_dismissed");
      pushDataLayer("Activation", "Checklist Dismissed");
    }
  }

  function restore() {
    if (typeof window !== "undefined" && userId) {
      localStorage.removeItem(DISMISS_KEY(userId));
      setIsDismissed(false);
      trackFunnel("activation", "checklist_restored");
      pushDataLayer("Activation", "Checklist Restored");
    }
  }

  return {
    hasMedia, hasUsedChat, hasCalendar, hasTeammate,
    completedCount, totalSteps, allComplete,
    orderedStepIds, onboardingGoal, goalBucket,
    isDismissed, isLoading, isError, dismiss, restore,
  };
}
